import { DecimalDigits } from '@constants/enums';
import { Button } from '@efexteam/components';
import { useRole } from '@efexteam/permission-manager';
import { Alert, Grid, Typography } from '@mui/material';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FEATURES_APP from '../../../../configs/featureflags';
import useCurrencyBalance from '../../../../hooks/useCurrencyBalance';
import {
  DECIMALS_TO_SHOW,
  formatCurrency,
  formatCurrencyToNumber,
  getCurrencySymbol,
} from '../../../../utils/formater';
import { AMOUNT_MONEY } from '../constants/catalogs';
import { useFormData } from '../context';
import useForwardPayment from '../hooks/useForwardPayment';
import { PaymentMethods, PaymentMethodsState } from '../types';

interface MethodProps {
  nextFormStep: () => void;
  prevFormStep: () => void;
}

type FormData = {
  comments: string;
};

const Method = ({ nextFormStep, prevFormStep }: MethodProps) => {
  const { t } = useTranslation();
  const { isPaymentAnalyst } = useRole();

  const { setFormValues, data } = useFormData();

  const {
    feeDescription,
    amount,
    client: clientSelected,
    clientCurrency,
    currency,
    resultAmount,
    reverse,
  } = data;

  const { balance: currencyBalance } = useCurrencyBalance({ currency });
  const [separateFee, setSeparateFee] = useState(AMOUNT_MONEY.ZERO_NUMBER);

  const newAccountBalance = useMemo(() => {
    const totalFees = feeDescription
      ? feeDescription.list_fees
          .filter((fee) => fee.charge_separate)
          .reduce(
            (sum, fee) => sum + Number(fee.amount),
            AMOUNT_MONEY.ZERO_NUMBER
          )
      : AMOUNT_MONEY.ZERO_NUMBER;
    setSeparateFee(totalFees);
    const totalBalance = Number(
      formatCurrencyToNumber(currencyBalance).toFixed(DECIMALS_TO_SHOW)
    );
    return totalBalance - Number(!reverse ? amount : resultAmount) - totalFees;
  }, [amount, currencyBalance, resultAmount, reverse, feeDescription]);

  const { isForwardVisible } = useForwardPayment({
    currency,
    desCurrency: clientCurrency,
    contactTypeId: clientSelected?.contactTypeId,
  });

  const [changeValue, setChangeValue] = useState(PaymentMethods.bank);

  const { handleSubmit } = useForm<FormData>();

  const handleChange = (e: any) => {
    const { value } = e.target;
    setChangeValue(value);
    setFormValues({
      ...data,
      paymentMethod:
        PaymentMethodsState[value as keyof typeof PaymentMethodsState],
      isPendingTransfer: value === PaymentMethods.bank,
      isSchedule: value === PaymentMethods.schedule,
      isForward: value === PaymentMethods.forward,
    });
  };

  const onSubmit = handleSubmit(() => {
    setFormValues({
      ...data,
      paymentMethod:
        PaymentMethodsState[changeValue as keyof typeof PaymentMethodsState],
      isPendingTransfer: changeValue === PaymentMethods.bank,
      isSchedule: changeValue === PaymentMethods.schedule,
      isForward: changeValue === PaymentMethods.forward,
    });
    nextFormStep();
  });

  useEffect(() => {
    if (data.paymentMethod) {
      setChangeValue(
        PaymentMethods[data.paymentMethod as keyof typeof PaymentMethods]
      );
    }
  }, [data]);

  useEffect(() => {
    setChangeValue(
      newAccountBalance < 0 ? PaymentMethods.bank : PaymentMethods.balance
    );
  }, [newAccountBalance]);

  if (!amount) return null;

  return (
    <form onSubmit={onSubmit} autoComplete="off">
      <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
        <Grid item xs={12} className="flex justify-center">
          <div className="text-center m-auto w-full w-100">
            <Typography variant="h6" className="!my-4 text-center">
              {t('paymentMethods')}
            </Typography>
            <div className="checkbox-methods">
              <div
                className={`checkbox-methods__option ${
                  newAccountBalance < 0 ? 'opacity-50 pointer-events-none' : ''
                }
              ${changeValue === 'checkbox-account' ? 'selected' : ''}`}
              >
                <label htmlFor="radio-1" className="radio-label">
                  <Typography className="radio-label-1">
                    {t('noAdditionalCost')}.
                  </Typography>
                  <Typography className="radio-label-2">
                    {t('balanceYourAccount')}.
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    className="radio-label-amount max-w-[150px] md:max-w-[400px] !mb-2 p-1"
                  >
                    {` ${currencyBalance} - ${getCurrencySymbol(currency)}${
                      !reverse ? amount : resultAmount
                    } ${
                      separateFee > AMOUNT_MONEY.ZERO_NUMBER
                        ? `- ${getCurrencySymbol(
                            currency
                          )}${separateFee.toFixed(2)}`
                        : ''
                    } = ${getCurrencySymbol(
                      currency
                    )}${newAccountBalance.toFixed(2)} ${currency}`}
                  </Typography>
                  {feeDescription?.list_fees.map((fee) =>
                    fee.amount !== AMOUNT_MONEY.ZERO_NUMBER ? (
                      <p key={fee.strategy_code} className="text-xs">
                        {t(fee.description || '')}
                      </p>
                    ) : null
                  )}
                  {newAccountBalance < 0 && (
                    <Alert severity="info" className="mb-2 text-left mr-12">
                      <span className="m-0 ml-1 font-bold">
                        {t('noteFunding', {
                          AMOUNT_CHANGE_CONTENT: `${formatCurrency(
                            -newAccountBalance,
                            DecimalDigits.DEFAULT_DIGITS,
                            DecimalDigits.DEFAULT_MIN_DIGITS,
                            currency
                          )}`,
                        })}
                      </span>
                    </Alert>
                  )}
                </label>
                <input
                  id="radio-1"
                  className="radio-custom"
                  name="radio-group"
                  type="radio"
                  disabled={newAccountBalance < 0}
                  value={PaymentMethods.balance}
                  onChange={handleChange}
                  checked={changeValue === 'checkbox-account'}
                />
              </div>
            </div>
            <div className="checkbox-methods">
              <div
                className={clsx(
                  'checkbox-methods__option',
                  newAccountBalance >= 0.0
                    ? 'opacity-50 pointer-events-none'
                    : '',
                  changeValue === 'checkbox-account' ? 'selected' : ''
                )}
              >
                <label htmlFor="radio-2" className="radio-label">
                  <Typography className="radio-label-1">
                    {t('noAdditionalCost')}.
                  </Typography>
                  <Typography className="radio-label-2">
                    {t('domesticTransferYourBank')}
                  </Typography>
                  <Alert severity="info" className="mb-2 mr-12 text-left">
                    <span className="m-0">{t('expireDeferred')}</span>
                  </Alert>
                </label>
                <input
                  id="radio-2"
                  className="radio-custom"
                  name="radio-group"
                  type="radio"
                  value={PaymentMethods.bank}
                  onChange={handleChange}
                  checked={changeValue === PaymentMethods.bank}
                />
              </div>
            </div>
            {FEATURES_APP.paymentSchedule && (
              <div className="relative mt-[10px] !hidden">
                <div
                  className={`checkbox-methods__option ${
                    newAccountBalance <= 0 || newAccountBalance < 0
                      ? 'opacity-50 pointer-events-none'
                      : ''
                  }
              ${changeValue === PaymentMethods.schedule ? 'selected' : ''}`}
                >
                  <label htmlFor="radio-3" className="radio-label">
                    <Typography className="radio-label-1">
                      {t('noAdditionalCost')}.
                    </Typography>
                    <Typography className="radio-label-2">
                      {t('schedulePayment')}
                    </Typography>
                    {newAccountBalance <= 0 ? (
                      <div className="radio-label-error">
                        {t('insufficientBalance')}
                      </div>
                    ) : (
                      ''
                    )}
                  </label>

                  <input
                    id="radio-3"
                    className="radio-custom"
                    name="radio-group"
                    type="radio"
                    value={PaymentMethods.schedule}
                    onChange={handleChange}
                    checked={changeValue === PaymentMethods.schedule}
                  />
                </div>
              </div>
            )}
            {isForwardVisible ? (
              <div className="relative mt-[10px]">
                <div
                  className={`checkbox-methods__option ${
                    changeValue === PaymentMethods.forward ? 'selected' : ''
                  }`}
                >
                  <label htmlFor="radio-4" className="radio-label">
                    <Typography className="radio-label-2">
                      {t('forwardTitle')}
                    </Typography>
                  </label>

                  <input
                    id="radio-4"
                    className="radio-custom"
                    name="radio-group"
                    type="radio"
                    value={PaymentMethods.forward}
                    onChange={handleChange}
                    checked={changeValue === PaymentMethods.forward}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          className="flex justify-between !flex-col md:!flex-row gap-4"
        >
          <Button
            appearance="secondary"
            size="large"
            className="mt-4"
            onClick={prevFormStep}
          >
            {t('back')}
          </Button>
          <Button
            appearance="primary"
            size="large"
            className="mt-4"
            type="submit"
          >
            {t(
              isPaymentAnalyst && changeValue !== PaymentMethods.forward
                ? 'configureNewPayment'
                : 'continue'
            )}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default Method;
